<script setup lang="ts">
import { QInput } from 'quasar'
import { Color, Currency, CurrencyId } from '@previo/codebooks'
import { computed } from 'vue'
import { usePrevioCore } from '@previo/core'
import { CurrencyDisplay, useCurrencyInput } from 'vue-currency-input'
import { PSpaceSize } from '../space/types'
import PDivider from '../divider/PDivider.vue'
import PSpace from '../space/PSpace.vue'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import type { PValidationRule } from '../../types/types'
import { usePrevioDS } from '../../composables/helpers'
import LabelInputField from './components/LabelInputField.vue'
import { usePriceInputModel } from './composable/usePriceInputModel'
import { PPriceInputFieldPrecision } from './types'

type Props = {
  label?: null | string
  tooltip?: null | string
  placeholder?: null | string
  hint?: null | string
  currencyId: CurrencyId
  hideCurrency?: boolean
  showCurrencyCode?: boolean
  rules?: PValidationRule[]
  disable?: boolean
  readonly?: boolean
  autofocus?: boolean
  errorMessage?: null | string
  precision?: PPriceInputFieldPrecision
  cy: string
}

type Emits = {
  (e: 'change', value: number | null): void
}

const model = defineModel<number | null>({ required: true })

const props = withDefaults(defineProps<Props>(), {
  disable: false,
  readonly: false,
  autofocus: false,
  hideCurrency: false,
  showCurrencyCode: false,
  precision: PPriceInputFieldPrecision.TWO,
  rules() {
    return []
  },
})

const emits = defineEmits<Emits>()

const core = usePrevioCore()
const ds = usePrevioDS()

const currency = computed<Currency>(() => {
  return Currency.getById(props.currencyId)
})

const currencyIndicator = computed<string>(() => {
  return props.showCurrencyCode ? currency.value.code : currency.value.currencySymbol()
})

const { inputRef, formattedValue, numberValue, setValue } = useCurrencyInput(
  {
    currency: currency.value.code,
    locale: core.useLocale().get(),
    currencyDisplay: CurrencyDisplay.hidden,
    hideGroupingSeparatorOnFocus: false,
    precision: props.precision,
  },
  false,
)

const { onFocus, onBlur } = usePriceInputModel({
  model,
  displayedValue: numberValue,
  precision: computed(() => props.precision),
  setDisplayedValue: setValue,
  onChange: (value) => emits('change', value),
})

const qRules = computed(() => {
  return props.rules
    .map((rule) => {
      return () => rule(model.value)
    })
    .concat([() => ds.rules.databaseDecimal(model.value)])
})

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})
</script>

<template>
  <div>
    <label-input-field :label="label" :is-required="isRequired" :tooltip="tooltip" />

    <q-input
      ref="inputRef"
      v-model="formattedValue"
      :placeholder="placeholder"
      :error-message="errorMessage ?? undefined"
      :error="hasAnyErrors"
      :bg-color="Color.WHITE"
      :autofocus="autofocus"
      :disable="disable"
      :readonly="readonly"
      :class="{ 'ds-input-field--readonly': readonly }"
      :rules="qRules"
      :hint="hint ?? undefined"
      hide-bottom-space
      no-error-icon
      lazy-rules
      outlined
      @focus="onFocus"
      @blur="onBlur"
    >
      <template v-if="!props.hideCurrency" #append>
        <p-divider vertical inset />
        <p-space :mobile="PSpaceSize.SM" />
        <p-text-form-body :text="currencyIndicator" />
      </template>
    </q-input>
  </div>
</template>
